import {
  RangeBoundaries,
  RangeMax,
  RangeMin,
} from "instantsearch.js/es/connectors/range/connectRange";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import {
  useCurrentRefinements,
  useRange,
  useStats,
} from "react-instantsearch-core-v7";
import { MessageDescriptor } from "@lingui/core";
import { ArrowLeft } from "src/organisms/AlgoliaProductList/MobileFilterView/DrawerFilter/DrawerFilter.styled";
import {
  MobileClearAllButton,
  MobileFilterContainer,
} from "./MobileFilter.styled";
import { LabelNavigation } from "../LabelNavigation";
import classNames from "classnames";
import { Flex } from "@otrium/core";
import { Price } from "src/atoms/Price";
import { Text } from "@otrium/atoms";
import { ResultsButton } from "src/organisms/AlgoliaProductList/DesktopFilterVeiw/DesktopFilter.styled";
import { Trans } from "@lingui/macro";
import { useHandleSearchParams } from "src/hooks/use-handle-search-params";
import { Range } from "rc-slider";
import { theme } from "src/theme";

type MobileRangeFilterProps = {
  title: MessageDescriptor;
  isOpen: boolean;
  attribute: string;
  isMultiColumn?: boolean;
  toggleOpen: () => void;
  onClear: () => void;
  handleScrollToTop: () => void;
};

function MobileRangeFilter({
  attribute,
  isOpen,
  title,
  toggleOpen,
  onClear,
  handleScrollToTop,
}: MobileRangeFilterProps): JSX.Element {
  const { items } = useCurrentRefinements();
  const { nbHits: total } = useStats();
  const { refine, range } = useRange({ attribute });
  const { handleSearchParamsPrice } = useHandleSearchParams();

  const [rangeValue, setRangeValue] = useState<RangeBoundaries>([0, 0]);
  const [_, startTransition] = useTransition();

  const { minValue, maxValue, priceFilter } = useMemo(() => {
    const priceFilter = items.find((item) => item.attribute === attribute);
    const minValue =
      (priceFilter?.refinements.find((item) => item.operator === ">=")
        ?.value as RangeMin) || range.min;
    const maxValue =
      (priceFilter?.refinements.find((item) => item.operator === "<=")
        ?.value as RangeMax) || range.max;

    return { minValue, maxValue, priceFilter };
  }, [items, range.min, range.max, attribute]);

  useEffect(() => {
    setRangeValue([minValue, maxValue]);
  }, [minValue, maxValue]);

  const handleChange = useCallback(
    ([rangeMin, rangeMax]: number[]) =>
      startTransition(() => setRangeValue([rangeMin, rangeMax])),
    [startTransition]
  );

  useEffect(() => {
    const url = new URL(location.toString());
    const minPrice = Number(url.searchParams.get("min_price") || minValue);
    const maxPrice = Number(url.searchParams.get("max_price") || maxValue);

    refine([minPrice, maxPrice]);
  }, [refine, minValue, maxValue]);

  const handleRefine = useCallback(() => {
    refine(rangeValue);

    handleSearchParamsPrice({
      min: rangeValue[0] !== minValue ? rangeValue[0] : undefined,
      max: rangeValue[1] !== maxValue ? rangeValue[1] : undefined,
    });

    handleScrollToTop();
  }, [
    rangeValue,
    minValue,
    maxValue,
    refine,
    handleSearchParamsPrice,
    handleScrollToTop,
  ]);

  return (
    <MobileFilterContainer className={classNames({ ["open"]: isOpen })}>
      <LabelNavigation
        label={title}
        leftAdornment={
          <MobileClearAllButton className="no-padding" onClick={toggleOpen}>
            <ArrowLeft strokeWidth="2" />
          </MobileClearAllButton>
        }
        rightAdornment={
          priceFilter?.refinements.length || 0 > 0 ? (
            <MobileClearAllButton onClick={onClear}>
              <Trans>Clear all</Trans>
            </MobileClearAllButton>
          ) : undefined
        }
      />
      <div className="content-wrapper">
        <Flex my={2} px={2} justifyContent="space-between">
          <Text fontWeight={600}>
            <Price price={Math.floor(rangeValue[0] as number)} />
          </Text>
          <Text fontWeight={600}>
            <Price price={Math.ceil(rangeValue[1] as number)} />
          </Text>
        </Flex>

        <Flex px={2}>
          <Range
            trackStyle={[
              { backgroundColor: theme.colors.tone.black },
              { backgroundColor: theme.colors.tone.marble },
            ]}
            handleStyle={[
              {
                backgroundColor: theme.colors.tone.white,
                border: `2px solid ${theme.colors.tone.black}`,
                height: "22px",
                width: "22px",
                top: "1px",
                boxShadow: "none",
              },
              {
                backgroundColor: theme.colors.tone.white,
                border: `2px solid ${theme.colors.tone.black}`,
                height: "22px",
                width: "22px",
                top: "1px",
                boxShadow: "none",
              },
            ]}
            railStyle={{ backgroundColor: theme.colors.tone.marble }}
            onChange={handleChange}
            onAfterChange={handleRefine}
            defaultValue={[Number(range.min), Number(range.max)]}
            allowCross={false}
            min={Number(range.min)}
            max={Number(range.max)}
            value={[Number(rangeValue[0]), Number(rangeValue[1])]}
          />
        </Flex>

        <ResultsButton
          fullWidth
          variant="primary"
          onClick={toggleOpen}
          disabled={total <= 0}
          className="total-button"
        >
          <span>
            {total > 0 ? (
              <Trans>Show {total} results</Trans>
            ) : (
              <Trans>Close</Trans>
            )}
          </span>
        </ResultsButton>
      </div>
    </MobileFilterContainer>
  );
}

export { MobileRangeFilter };
